"use client"

import { create } from "zustand"
import { persist } from "zustand/middleware"

export interface TagAlias {
  id: string
  name: string
  aliases: string[]
  createdAt: number
}

interface TagAliasesStore {
  aliases: TagAlias[]
  createAlias: (name: string, aliasedTags: string[]) => void
  deleteAlias: (id: string) => void
  updateAlias: (id: string, aliasedTags: string[]) => void
  getAliasesForTag: (tag: string) => string[]
  getAliasGroup: (tag: string) => TagAlias | undefined
  expandTagWithAliases: (tag: string) => string[]
}

export const useTagAliasesStore = create<TagAliasesStore>()(
  persist(
    (set, get) => ({
      aliases: [],

      createAlias: (name: string, aliasedTags: string[]) => {
        const newAlias: TagAlias = {
          id: crypto.randomUUID(),
          name,
          aliases: aliasedTags.map((t) => t.toLowerCase().trim()),
          createdAt: Date.now(),
        }
        set((state) => ({
          aliases: [...state.aliases, newAlias],
        }))
      },

      deleteAlias: (id: string) => {
        set((state) => ({
          aliases: state.aliases.filter((a) => a.id !== id),
        }))
      },

      updateAlias: (id: string, aliasedTags: string[]) => {
        set((state) => ({
          aliases: state.aliases.map((a) =>
            a.id === id ? { ...a, aliases: aliasedTags.map((t) => t.toLowerCase().trim()) } : a,
          ),
        }))
      },

      getAliasesForTag: (tag: string) => {
        const cleanTag = tag.toLowerCase().trim()
        const aliasGroup = get().aliases.find((a) => a.aliases.includes(cleanTag))
        return aliasGroup ? aliasGroup.aliases : [cleanTag]
      },

      getAliasGroup: (tag: string) => {
        const cleanTag = tag.toLowerCase().trim()
        return get().aliases.find((a) => a.aliases.includes(cleanTag))
      },

      expandTagWithAliases: (tag: string) => {
        const aliases = get().getAliasesForTag(tag)
        return aliases
      },
    }),
    {
      name: "tag-aliases",
    },
  ),
)
